import React, { useEffect, useContext } from 'react';
import { useSelector, useDispatch } from 'react-redux';
// eslint-disable-next-line import/no-unresolved
import normalizeCss from 'normalize.css';
import withStyles from 'isomorphic-style-loader/withStyles';
import { makeStyles, createStyles } from '@material-ui/core';
import flagsmith from 'flagsmith';
import * as Sentry from '@sentry/react';
import { IconType } from 'react-icons';
import { PortalConfigContext, RouteContext } from '../../context';
import { RootState } from '../../store/reduxTypes';
import {
  initializeOnboardingTestData,
  loadAuthenticatedUser,
} from '../../store/user/actions';
import * as Colors from '../../theme/colors';
import {
  togglePrimarySidebarMobile,
  updateSearchValueAction,
} from '../../store/ui/actions';
import { ShortcutsSidebar } from '../ShortcutsDrawer';
// external-global styles must be imported in your JS.
import s from './Layout.css';
import {
  DESKTOP_APP_BAR_HEIGHT,
  MOBILE_APP_BAR_HEIGHT,
  BANNER_HEIGHT,
} from '../../constants/uiConsts';
import Navbar from '../Navbar';
import Sidebar from '../Sidebar';
import AuthModal from '../Auth/AuthModal';
import { HelpCenterModal } from './HelpCenterModal';
import { CompleteExternalActionModal } from '../Modals';
import { useAnonymousId } from '../../hooks/useAnonymousId';
import { LoadSettings } from '../../store/settings/actions';
import { CommandBar } from '../CommandBar';
import { GeneralCommandbarActionsInitializer } from '../CommandBar/GeneralCommandbarActionsInitializer';
import { useImportProgressNotifier } from '../../hooks/useImportProgressNotifier';
import { CLIENT_DETAILS_PAGE, CRM_PAGE } from '../../constants';

interface SpacingProps {
  disableGutters: boolean;
  showBanner: boolean;
}
const useStyles = makeStyles(theme =>
  createStyles({
    '@global': {
      '::-webkit-scrollbar': {
        width: '6px',
        height: '6px',
      },
      '::-webkit-scrollbar-track': {
        '-webkit-border-radius': '10px',
        borderRadius: '10px',
      },

      '::-webkit-scrollbar-thumb': {
        '-webkit-border-radius': '10px',
        'border-radius': '10px',
        background: Colors.ScrollbarColor,
      },
    },
    root: {
      fontFamily: theme.typography.fontFamily,
      display: 'flex',
      overflowY: 'auto',
      boxSizing: 'border-box',
      height: '100%',
      scrollbarColor: `${Colors.ScrollbarColor} transparent`,
      [theme.breakpoints.up('sm')]: {
        paddingTop: (props: SpacingProps) =>
          props.disableGutters
            ? 0
            : `${DESKTOP_APP_BAR_HEIGHT +
                (props.showBanner ? BANNER_HEIGHT : 0)}px`,
      },
      [theme.breakpoints.down('xs')]: {
        overflowY: 'hidden',
        '@supports ( -moz-appearance:none )': {
          overflowY: 'auto',
          scrollbarWidth: 'thin',
        },
        paddingTop: (props: SpacingProps) =>
          `${MOBILE_APP_BAR_HEIGHT + (props.showBanner ? BANNER_HEIGHT : 0)}px`,
      },
    },
  }),
);

export interface LayoutProps {
  title?: string;
  titleIcon?: IconType;
  hasNavbar?: boolean;
  showSearchbar?: boolean;
  showBreadCrumb?: boolean;
  BreadCrumbComponent?: React.FC;
  disableGutters?: boolean;
  hasSidebar?: boolean;
  showNavbarAuthActions?: boolean;
  headerComponent?: React.ReactNode;
}

const PAGES_WITH_RETAINED_SEARCH = [CRM_PAGE.path, CLIENT_DETAILS_PAGE.path];

const Layout: React.FC<LayoutProps> = props => {
  const {
    title,
    titleIcon,
    hasNavbar = true,
    showSearchbar = false,
    showBreadCrumb = false,
    BreadCrumbComponent,
    hasSidebar = true,
    children,
    disableGutters = false,
    showNavbarAuthActions = true,
    headerComponent,
  } = props;
  const layoutWrapperRef = React.useRef<HTMLDivElement>(null);
  const portalConfig = useContext(PortalConfigContext);
  const { pathname } = useContext(RouteContext);
  const dispatch = useDispatch();
  const savedAnonymousId = useAnonymousId();
  const user = useSelector((state: RootState) => state.user);
  const isPrimarySideBarMobileOpen = useSelector(
    (state: RootState) => state.ui.primarySideBarMobile.isOpen,
  );

  const bannerOptions = useSelector(
    (state: RootState) => state.ui.bannerOptions,
  );
  const isLoadingSettings = useSelector(
    (state: RootState) => state.settings.loading,
  );
  const isLoadedSettings = useSelector(
    (state: RootState) => state.settings.loaded,
  );
  const loadSettingsErr = useSelector(
    (state: RootState) => state.settings.loadError,
  );

  const classes = useStyles({
    disableGutters,
    showBanner: Boolean(bannerOptions),
  });

  const showPrimarySidebarMobile = React.useCallback(() => {
    dispatch(togglePrimarySidebarMobile({ isOpen: true }));
  }, []);

  const hidePrimarySidebarMobile = React.useCallback(() => {
    dispatch(togglePrimarySidebarMobile({ isOpen: false }));
  }, []);

  useImportProgressNotifier();

  /**
   * load settings when userID is available
   * and settings is not loaded
   * (including is currently or prev error loading)
   */
  useEffect(() => {
    if (
      user.id &&
      !isLoadingSettings &&
      !isLoadedSettings &&
      !loadSettingsErr
    ) {
      dispatch(LoadSettings(user.id));
    }
  }, [user.id, isLoadingSettings, isLoadedSettings, loadSettingsErr]);

  // this replaces component did mount hook
  useEffect(() => {
    if (!user.loaded && !user.loading && !user.loadingError) {
      dispatch(loadAuthenticatedUser(portalConfig));
    }

    if (user.loaded && user.id) {
      // initialize onboarding test data
      if (!user.isClient) {
        initializeOnboardingTestData();
      }

      // trigger identify calls
      if (user.instance) {
        if (analytics && analytics.identify && analytics.group) {
          if (savedAnonymousId) {
            analytics.setAnonymousId(savedAnonymousId);
          }

          analytics.identify(
            user.id,
            {
              name: `${user.instance?.attributes.given_name} ${user.instance?.attributes.family_name}`,
              email: user.instance?.attributes.email,
              userType: user.isClient ? 'client' : 'internal',
              portalURL: window.location.origin,
            },
            {
              integrations: {
                'Customer.io': !user.isClient,
              },
            },
          );
          analytics.group(
            portalConfig.portalHeader,
            {
              groupType: 'Portal',
              name: portalConfig.name,
            },
            {
              integrations: {
                'Customer.io': !user.isClient,
                'Customer.io Actions': !user.isClient,
              },
            },
          );
        }
        flagsmith.identify(user.id);
        flagsmith.setTrait('portal_id', portalConfig.portalHeader);
        Sentry.setUser({
          id: user.id,
          email: user.instance?.attributes.email,
          segment: portalConfig.portalHeader,
        });
      }
    }
  }, [user.id, user.loaded, user.loading, user.loadingError]);

  const renderChildren = React.Children.map(children, child =>
    React.cloneElement(child, { user }),
  );

  /**
   * Searchbar should persist the search query when navigating between client => client details and vice versa.
   * Resets the searchbar if history stack does not indicate that navigation occured between client and client detail.
   * Logic resides in Layout component because `Searchbox` component isn't always preset in DOM
   */
  React.useEffect(() => {
    if (!PAGES_WITH_RETAINED_SEARCH.includes(pathname)) {
      dispatch(updateSearchValueAction(''));
    }
    return () => {
      if (!PAGES_WITH_RETAINED_SEARCH.includes(pathname)) {
        dispatch(updateSearchValueAction(''));
      }
    };
  }, [pathname]);

  React.useEffect(() => {
    if (layoutWrapperRef.current) {
      layoutWrapperRef.current.scrollTop = 0;
    }
  }, [children]);

  return (
    <div className={classes.root} ref={layoutWrapperRef}>
      <CommandBar>
        <HelpCenterModal />
        {hasNavbar && (
          <Navbar
            title={title}
            titleIcon={titleIcon}
            user={user}
            onOpenSidebarMobile={showPrimarySidebarMobile}
            showSearchbar={showSearchbar}
            showBreadCrumb={showBreadCrumb}
            BreadCrumbComponent={BreadCrumbComponent}
            showNavbarAuthActions={showNavbarAuthActions}
            headerContent={headerComponent}
          />
        )}
        {hasSidebar && (
          <Sidebar
            user={user}
            onMobileClose={hidePrimarySidebarMobile}
            openMobile={isPrimarySideBarMobileOpen}
          />
        )}
        {renderChildren}
        <AuthModal />
        {user.loaded && <CompleteExternalActionModal />}
        {user.loaded && <GeneralCommandbarActionsInitializer />}
        <ShortcutsSidebar />
      </CommandBar>
    </div>
  );
};

export default withStyles(normalizeCss, s)(Layout);
